import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Badge,
  Button,
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Spinner,
  TableCellLayout,
  TableColumnDefinition,
  Text,
} from '@fluentui/react-components';
import { Add24Regular, Delete24Regular, Edit24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { ContractNegotiationVersionConcept } from '../../../models/Entities/Contracts/ContractNegotiationVersionConcept';
import { NewContractNegotiationVersionConcept } from '../../../models/Entities/Contracts/NewContractNegotiationVersionConcept';
import * as ComponentFunctions from './ContractNegotiationVersionConceptList.Functions';
import { useContractNegotiationVersionConceptListStyles } from './ContractNegotiationVersionConceptList.Styles';
import ContractNegotiationVersionConceptFormComponent from '../ContractNegotiationVersionConceptForm/ContractNegotiationVersionConceptForm.Component';
import { mapStateToProps, mapDispatchToProps } from './ContractNegotiationVersionConceptList.Functions';

type BadgeColor = 'warning' | 'success' | 'danger' | 'informative';

interface ConceptStatusInfo {
  label: string;
  color: BadgeColor;
}

const getConceptStatusInfo = (
  status: number,
  t: (key: string, fallback: string) => string
): ConceptStatusInfo => {
  if (status === 0) return { label: t('contractNegotiationVersionConceptList.status.pending', 'Pendiente'), color: 'warning' };
  if (status === 1) return { label: t('contractNegotiationVersionConceptList.status.accepted', 'Aceptado'), color: 'success' };
  if (status === 2) return { label: t('contractNegotiationVersionConceptList.status.rejected', 'Rechazado'), color: 'danger' };
  return { label: String(status), color: 'informative' };
};

const formatCost = (value: number | undefined): string => {
  if (typeof value !== 'number') return '-';
  return value.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
};

const formatVariance = (value: number | undefined): string => {
  if (typeof value !== 'number') return '-';
  return `${value.toFixed(2)}%`;
};

const ContractNegotiationVersionConceptListComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const styles = useContractNegotiationVersionConceptListStyles();

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [selectedVersionConcept, setSelectedVersionConcept] = useState<
    ContractNegotiationVersionConcept | undefined
  >(undefined);
  const [deleteTarget, setDeleteTarget] = useState<ContractNegotiationVersionConcept | undefined>(undefined);

  useEffect(() => {
    props.GetContractNegotiationVersionConcepts(
      props.contractNegotiationVersionId,
      props.contractNegotiationId,
      props.contractId
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.contractNegotiationVersionId, props.contractNegotiationId, props.contractId]);

  const handleAddNew = () => {
    setSelectedVersionConcept(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (vc: ContractNegotiationVersionConcept) => {
    setSelectedVersionConcept(vc);
    setIsFormOpen(true);
  };

  const handleFormDismiss = () => {
    setIsFormOpen(false);
    setSelectedVersionConcept(undefined);
  };

  const handleSave = (
    entity: NewContractNegotiationVersionConcept | ContractNegotiationVersionConcept
  ) => {
    const isExisting = 'id' in entity && !!(entity as ContractNegotiationVersionConcept).id;
    if (isExisting) {
      props.UpdateContractNegotiationVersionConcept(
        entity as ContractNegotiationVersionConcept,
        props.contractId,
        props.contractNegotiationId
      );
    } else {
      props.CreateContractNegotiationVersionConcept(
        entity as NewContractNegotiationVersionConcept,
        props.contractId,
        props.contractNegotiationId
      );
    }
    setIsFormOpen(false);
    setSelectedVersionConcept(undefined);
  };

  const handleDeleteRequest = (vc: ContractNegotiationVersionConcept) => {
    setDeleteTarget(vc);
  };

  const handleDeleteConfirm = () => {
    if (deleteTarget) {
      props.DeleteContractNegotiationVersionConcept(
        deleteTarget.id,
        props.contractId,
        props.contractNegotiationId,
        deleteTarget.contractNegotiationVersionId
      );
    }
    setDeleteTarget(undefined);
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(undefined);
  };

  const columns: TableColumnDefinition<ContractNegotiationVersionConcept>[] = useMemo(
    () => [
      createTableColumn<ContractNegotiationVersionConcept>({
        columnId: 'contractNegotiationConceptId',
        renderHeaderCell: () =>
          t('contractNegotiationVersionConceptList.columns.conceptId', 'ID Concepto neg.'),
        renderCell: (item) => <TableCellLayout>{item.contractNegotiationConceptId}</TableCellLayout>,
      }),
      createTableColumn<ContractNegotiationVersionConcept>({
        columnId: 'currencyCode',
        renderHeaderCell: () =>
          t('contractNegotiationVersionConceptList.columns.currency', 'Moneda'),
        renderCell: (item) => <TableCellLayout>{item.currencyCode || '-'}</TableCellLayout>,
      }),
      createTableColumn<ContractNegotiationVersionConcept>({
        columnId: 'sponsorCost',
        renderHeaderCell: () =>
          t('contractNegotiationVersionConceptList.columns.sponsorCost', 'Costo sponsor'),
        renderCell: (item) => <TableCellLayout>{formatCost(item.sponsorCost)}</TableCellLayout>,
      }),
      createTableColumn<ContractNegotiationVersionConcept>({
        columnId: 'sponsorReferenceVariance',
        renderHeaderCell: () =>
          t('contractNegotiationVersionConceptList.columns.sponsorVariance', 'Var. sponsor'),
        renderCell: (item) => (
          <TableCellLayout>{formatVariance(item.sponsorReferenceVariance)}</TableCellLayout>
        ),
      }),
      createTableColumn<ContractNegotiationVersionConcept>({
        columnId: 'siteCost',
        renderHeaderCell: () =>
          t('contractNegotiationVersionConceptList.columns.siteCost', 'Costo sitio'),
        renderCell: (item) => <TableCellLayout>{formatCost(item.siteCost)}</TableCellLayout>,
      }),
      createTableColumn<ContractNegotiationVersionConcept>({
        columnId: 'siteReferenceVariance',
        renderHeaderCell: () =>
          t('contractNegotiationVersionConceptList.columns.siteVariance', 'Var. sitio'),
        renderCell: (item) => (
          <TableCellLayout>{formatVariance(item.siteReferenceVariance)}</TableCellLayout>
        ),
      }),
      createTableColumn<ContractNegotiationVersionConcept>({
        columnId: 'negotiationConceptStatus',
        renderHeaderCell: () =>
          t('contractNegotiationVersionConceptList.columns.status', 'Estado'),
        renderCell: (item) => {
          const info = getConceptStatusInfo(item.negotiationConceptStatus, t);
          return (
            <TableCellLayout>
              <Badge appearance="tint" color={info.color} className={styles.statusBadge}>
                {info.label}
              </Badge>
            </TableCellLayout>
          );
        },
      }),
      createTableColumn<ContractNegotiationVersionConcept>({
        columnId: 'actions',
        renderHeaderCell: () =>
          t('contractNegotiationVersionConceptList.columns.actions', 'Acciones'),
        renderCell: (item) => (
          <TableCellLayout>
            <div className={styles.actionCell}>
              <Button
                appearance="subtle"
                size="small"
                icon={<Edit24Regular />}
                onClick={() => handleEdit(item)}
                title={t('contractNegotiationVersionConceptList.actions.edit', 'Editar')}
              />
              <Button
                appearance="subtle"
                size="small"
                icon={<Delete24Regular />}
                onClick={() => handleDeleteRequest(item)}
                title={t('contractNegotiationVersionConceptList.actions.delete', 'Eliminar')}
              />
            </div>
          </TableCellLayout>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, styles]
  );

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Text weight="semibold" size={300}>
          {t('contractNegotiationVersionConceptList.title', 'Conceptos de versión')}
        </Text>
        <Button appearance="primary" size="small" icon={<Add24Regular />} onClick={handleAddNew}>
          {t('contractNegotiationVersionConceptList.addButton', 'Agregar concepto')}
        </Button>
      </div>

      {props.isLoading && (
        <Spinner
          size="small"
          label={t('contractNegotiationVersionConceptList.loading', 'Cargando conceptos...')}
        />
      )}

      {props.errorMessage && (
        <MessageBar intent="error">
          <MessageBarBody>{props.errorMessage}</MessageBarBody>
        </MessageBar>
      )}

      {!props.isLoading && !props.errorMessage && props.versionConcepts.length === 0 && (
        <div className={styles.emptyState}>
          <Text>
            {t('contractNegotiationVersionConceptList.empty', 'No hay conceptos registrados.')}
          </Text>
        </div>
      )}

      {props.versionConcepts.length > 0 && (
        <div className={styles.gridWrapper}>
          <DataGrid
            items={props.versionConcepts}
            columns={columns}
            sortable
            resizableColumns
            focusMode="composite"
            getRowId={(item) => item.id}
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<ContractNegotiationVersionConcept>>
              {({ item, rowId }) => (
                <DataGridRow<ContractNegotiationVersionConcept> key={rowId}>
                  {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        </div>
      )}

      <ContractNegotiationVersionConceptFormComponent
        contractNegotiationVersionId={props.contractNegotiationVersionId}
        contractNegotiationId={props.contractNegotiationId}
        contractId={props.contractId}
        versionConcept={selectedVersionConcept}
        isOpen={isFormOpen}
        onDismiss={handleFormDismiss}
        onSave={handleSave}
      />

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(_, data) => {
          if (!data.open) handleDeleteCancel();
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {t(
                'contractNegotiationVersionConceptList.deleteDialog.title',
                'Eliminar concepto de versión'
              )}
            </DialogTitle>
            <DialogContent>
              {t(
                'contractNegotiationVersionConceptList.deleteDialog.message',
                '¿Está seguro que desea eliminar este concepto de la versión?'
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleDeleteCancel}>
                {t('contractNegotiationVersionConceptList.deleteDialog.cancel', 'Cancelar')}
              </Button>
              <Button appearance="primary" onClick={handleDeleteConfirm}>
                {t('contractNegotiationVersionConceptList.deleteDialog.confirm', 'Eliminar')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export { ContractNegotiationVersionConceptListComponent };
export default connect(mapStateToProps, mapDispatchToProps)(ContractNegotiationVersionConceptListComponent as React.ComponentType<ComponentFunctions.OwnProps>);
